import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2, Wifi, ShieldCheck, PenTool, Eraser, AlertTriangle } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-slate-800 bg-slate-950 text-slate-100 placeholder:text-slate-600 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors";
const STEPS = ["Plan", "Options & add-ons", "You", "Address", "Sign & submit"];

// NBN technology catalogue for the wizard's step-0 tech picker. Grouped
// so a customer instinctively picks Fixed line vs Fixed Wireless before
// seeing the plan grid, keeping unavailable plans out of view. Labels
// mirror NBN Co's public terminology so a customer copying from their
// address-check page sees the same words.
const NBN_TECHS_FIXED = [
  { code: "FTTP", label: "FTTP",  desc: "Fibre to the Premises" },
  { code: "HFC",  label: "HFC",   desc: "Hybrid Fibre Coaxial" },
  { code: "FTTB", label: "FTTB",  desc: "Fibre to the Building" },
  { code: "FTTC", label: "FTTC",  desc: "Fibre to the Curb" },
  { code: "FTTN", label: "FTTN",  desc: "Fibre to the Node" },
];
const NBN_TECHS_WIRELESS = [
  { code: "FW",   label: "Fixed Wireless", desc: "NBN Fixed Wireless (rural + regional)" },
];
const NBN_TECH_LABEL = Object.fromEntries(
  [...NBN_TECHS_FIXED, ...NBN_TECHS_WIRELESS].map(t => [t.code, t.label])
);

// Tech picker — rendered on step 0 before the plan grid. Groups NBN
// technologies under Fixed vs Wireless so a customer picks the type
// their premise has been provisioned for. Selection narrows the plan
// grid to plans that list that tech in ``technologies`` (or plans
// with no tech list, treated as "available anywhere").
function TechPicker({ onPick }) {
  return (
    <div data-testid="tech-picker" className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-1">
          Step 1 of 5
        </div>
        <h3 className="font-display text-2xl">Which NBN technology is at your address?</h3>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
          Not sure? Look up your address on the{" "}
          <a
            href="https://www.nbnco.com.au/connect-home-or-business/check-your-address"
            target="_blank" rel="noopener noreferrer"
            className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200"
          >
            Check address | nbn
          </a>{" "}
          page &mdash; the technology type is shown alongside your address.
        </p>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
          Fixed line
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {NBN_TECHS_FIXED.map(t => (
            <button
              key={t.code}
              type="button"
              data-testid={`tech-pick-${t.code}`}
              onClick={() => onPick(t.code)}
              className="text-left rounded-2xl border border-slate-800 bg-slate-900 hover:border-cyan-500 hover:bg-cyan-500/5 transition-colors p-5"
            >
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-cyan-400" />
                <div className="font-display text-xl">{t.label}</div>
              </div>
              <div className="text-xs text-slate-500 mt-2 leading-relaxed">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
          Wireless
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {NBN_TECHS_WIRELESS.map(t => (
            <button
              key={t.code}
              type="button"
              data-testid={`tech-pick-${t.code}`}
              onClick={() => onPick(t.code)}
              className="text-left rounded-2xl border border-slate-800 bg-slate-900 hover:border-cyan-500 hover:bg-cyan-500/5 transition-colors p-5"
            >
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-cyan-400" />
                <div className="font-display text-xl">{t.label}</div>
              </div>
              <div className="text-xs text-slate-500 mt-2 leading-relaxed">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function InternetSignup() {
  // Partner-attributed signup links must never appear in search results —
  // they carry per-partner attribution and should be handed out privately,
  // not discovered. Set noindex,nofollow at the document level as soon as
  // this page mounts, and restore the default on unmount so the marketing
  // landing keeps its normal indexable state.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive, nosnippet";
    tag.setAttribute("data-signup-noindex", "1");
    document.head.appendChild(tag);
    return () => {
      const el = document.head.querySelector('meta[data-signup-noindex="1"]');
      if (el) el.remove();
    };
  }, []);

  // Partner attribution can come from three URL shapes so any legacy link
  // still works:
  //   /internet/signup/<partnerCode>            (preferred — used by partner portal)
  //   /internet/signup?partner=<partnerCode>    (query-param fallback)
  //   /signup/<slug>                            (legacy migration/public form path)
  const params = useParams();
  const [searchParams] = useSearchParams();
  const slug = params.partnerCode || params.slug
    || searchParams.get("partner") || searchParams.get("partner_code") || null;
  // Two flows share the same wizard: `transfer` (customer porting from a
  // competitor onto our network — typically wholesale-mirrored partner
  // pricing) and `new` (fresh signup with retail plans). Reading it from the
  // URL keeps the partner short-URLs simple: /signup/mack/transfer,
  // /signup/mack/new.
  const flowParam = (params.flow || searchParams.get("flow") || "").toLowerCase();
  const flow = flowParam === "transfer" || flowParam === "new" ? flowParam : null;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [plans, setPlans] = useState([]);
  const [staticIp, setStaticIp] = useState(null);
  // Technology filter — Step 0 asks the customer which NBN technology
  // their premise has (grouped Fixed vs Wireless) before showing plans.
  // Falsy = tech picker shown; truthy string = plans filtered to that
  // technology. Falls through to "show all" for plans that don't list
  // ``technologies`` explicitly (i.e. "available on any tech").
  const [techFilter, setTechFilter] = useState("");
  const [legals, setLegals] = useState([]);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    plan_id: "", business_name: "", contact_name: "", email: "", phone: "",
    abn: "", service_address: "", billing_address: "",
    // AVC number — collected on the Macktronix `/new` flow when the customer
    // is porting in from a non-Valve provider. Optional (fresh installs won't
    // have one; existing Valve customers come via `/transfer` where we
    // already hold the port record).
    avc_number: "",
    // Static IP tri-state — ``null`` = customer hasn't answered yet
    // (Options step gates ``canNext`` on this being non-null), ``true``
    // / ``false`` = explicit yes / no. ``static_ip_addon`` mirrors the
    // yes/no answer so downstream code (submit body, StepSign summary,
    // pricing math) keeps its existing boolean shape.
    static_ip_choice: null,
    static_ip_addon: false,
    typed_name: "", drawn_data_url: null, agreed: false,
  });
  // Show the AVC field on the /new flow only when the partner actually
  // operates a port list from a specific upstream RSP (i.e. the
  // transfer flow is available on this partner). If a new customer is
  // porting in from a NON-upstream provider, we need their AVC. Kept
  // partner-agnostic — driven purely by the ``transfer_upstream_name``
  // configured on the partner in Admin → Partners.
  const showAvcField = flow === "new" && !!(meta?.partner_transfer_upstream || "").trim();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [legalOpen, setLegalOpen] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams();
    if (slug) q.set("partner_code", slug);
    if (flow) q.set("flow", flow);
    const qs = q.toString() ? `?${q.toString()}` : "";
    Promise.all([
      axios.get(`${API}/public/internet-plans${qs}`),
      axios.get(`${API}/public/legal-docs`),
      slug ? axios.get(`${API}/public/signup/${slug}`).catch(() => ({data:{}})) : Promise.resolve({data:{}}),
    ]).then(([p, l, m]) => {
      setPlans(p.data.plans || []);
      setStaticIp(p.data.static_ip_addon || null);
      setLegals(l.data.docs || []);
      setMeta(m.data || {});
    }).catch(() => toast.error("Failed to load signup page"));
  }, [slug, flow]);

  const chosenPlan = useMemo(() => plans.find(p => p.id === form.plan_id), [plans, form.plan_id]);
  // Plans filtered by the chosen NBN technology. Plans without a
  // ``technologies`` array (or empty) count as available-on-any so they
  // always survive the filter — that's how you'd flag e.g. Fixed
  // Wireless-Plus which currently isn't tagged. When ``techFilter`` is
  // falsy (before the customer picks) the whole plan grid is hidden and
  // the tech picker renders instead.
  const filteredPlans = useMemo(() => {
    if (!techFilter) return [];
    return plans.filter(p => {
      const t = p.technologies || [];
      if (!t.length) return true;
      return t.includes(techFilter);
    });
  }, [plans, techFilter]);
  const canNext = useMemo(() => {
    if (step === 0) return !!(techFilter && form.plan_id);
    // Step 1 (Options & add-ons): customer MUST explicitly opt in or
    // out of every add-on we surface so no one gets a "surprise no"
    // — ``static_ip_choice`` is a tri-state (null/true/false) with
    // no default; picking either card makes it non-null. If the
    // tenant hasn't enabled static IP, this gate is automatically
    // satisfied so the step still passes.
    if (step === 1) {
      if (staticIp?.enabled) return form.static_ip_choice === true || form.static_ip_choice === false;
      return true;
    }
    // v1.10.102: business_name is optional (personal customers don't
    // have one). Contact name + email remain required — we chase the
    // customer through them.
    if (step === 2) return !!(form.contact_name && form.email);
    if (step === 3) {
      if (!form.service_address) return false;
      // New-connection flow requires a proof-of-address upload before
      // the customer can advance to Sign & submit — matches the paper
      // process ops already runs manually post-signup.
      if (flow === "new" && !form.proof_of_address?.upload_id) return false;
      return true;
    }
    if (step === 4) return !!(form.typed_name && form.agreed && form.drawn_data_url);
    return false;
  }, [step, form, techFilter, staticIp]);

  async function submit() {
    setBusy(true);
    try {
      const body = { ...form, partner_code: slug || form.partner_code || null,
                      flow_type: flow || null };
      const { data } = await axios.post(`${API}/internet/signup`, body);
      setDone(data);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Signup failed");
    } finally { setBusy(false); }
  }

  if (done) return <SuccessCard result={done} onBack={() => navigate("/")} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{color: meta?.partner_brand_color || "#22d3ee"}}>
            {meta?.brand_name || "Steele Cloud"} {slug ? `· ${meta?.partner_name || slug}` : ""}
          </div>
          {slug && meta?.partner_logo_url && (
            <img
              src={meta.partner_logo_url}
              alt={meta?.partner_name || slug}
              className="h-12 mt-3 rounded-md bg-white/5 border border-slate-800 p-1"
              data-testid="partner-branding-logo"
            />
          )}
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3">
            {flow === "transfer" ? "Transfer your NBN" : flow === "new" ? "New NBN connection" : "Sign up for NBN"}
          </h1>
          {flow === "transfer" && step === 0 && (
            <p className="text-base text-slate-300 mt-3 max-w-2xl" data-testid="transfer-flow-instruction">
              Please select your current plan to transfer.
            </p>
          )}
          {/* Per-partner-per-flow custom intro copy — freeform sanitised
              HTML from Admin → Partners → the ``Signup intro`` fields.
              Rendered under the H1 (and above the AVC-upstream banner
              below) so partners can add their own pitch/reassurance
              without a code change. Renders nothing when blank. */}
          {step === 0 && flow === "transfer" && (meta?.signup_transfer_intro_html || "").trim() && (
            <div
              data-testid="signup-transfer-intro"
              className="mt-4 max-w-2xl text-slate-300 text-sm leading-relaxed
                         [&_a]:text-cyan-300 [&_a]:underline [&_a]:underline-offset-4
                         [&_b]:text-slate-100 [&_strong]:text-slate-100
                         [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                         [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                         [&_li]:my-1 [&_p]:my-2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(meta.signup_transfer_intro_html, {
                  USE_PROFILES: { html: true },
                }),
              }}
            />
          )}
          {step === 0 && flow === "new" && (meta?.signup_new_intro_html || "").trim() && (
            <div
              data-testid="signup-new-intro"
              className="mt-4 max-w-2xl text-slate-300 text-sm leading-relaxed
                         [&_a]:text-cyan-300 [&_a]:underline [&_a]:underline-offset-4
                         [&_b]:text-slate-100 [&_strong]:text-slate-100
                         [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                         [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                         [&_li]:my-1 [&_p]:my-2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(meta.signup_new_intro_html, {
                  USE_PROFILES: { html: true },
                }),
              }}
            />
          )}
          {/* Amber banner on any partner-transfer flow whose upstream
              provider we know. Copy dynamically reflects the upstream so
              the same code serves Macktronix (Valve) and any future
              partner (e.g. someone porting from Aussie Broadband) with
              a text-only edit on Admin → Partners.

              v1.10.103: locked down to "existing {upstream} / {partner}
              customers only" and dropped the link to the new-customer
              signup — this form is for existing customers only, and
              linking out was confusing customers who thought they
              needed to fill in both. Someone who's genuinely a new
              customer should be coming in via the partner's new-signup
              URL directly, not bouncing off this transfer form. */}
          {flow === "transfer" && (meta?.partner_transfer_upstream || "").trim() && (
            <div
              data-testid="transfer-valve-only-banner"
              className="mt-4 rounded-lg border-2 border-amber-500/60 bg-amber-500/10 p-4 flex items-start gap-3 max-w-2xl"
            >
              <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100 leading-relaxed">
                <b className="text-amber-200">
                  Transfers for existing {meta.partner_transfer_upstream}
                  {" "}/ {meta?.partner_short_name || meta?.partner_name || slug} customers only.
                </b>{" "}
                This form ports customers currently on{" "}
                <span className="font-semibold text-amber-100">{meta.partner_transfer_upstream}</span> —
                {" "}{meta?.partner_short_name || meta?.partner_name || slug}&apos;s previous
                wholesale supplier. If you&apos;re on any other provider or you&apos;re
                a brand-new customer, please reach out to{" "}
                {meta?.partner_short_name || meta?.partner_name || slug} directly
                rather than using this form.
              </div>
            </div>
          )}
          {slug && meta?.partner_tagline && (
            <p className="text-sm text-slate-400 mt-2 max-w-2xl italic">&ldquo;{meta.partner_tagline}&rdquo;</p>
          )}
          {slug && meta?.partner_short_name && (
            <p className="text-xs text-slate-500 mt-2" data-testid="partner-offered-by-note">
              Steele Cloud NBN — as offered by <b className="text-slate-300">{meta.partner_name || meta.partner_short_name}</b>. Any signup completed here is attributed to them.
            </p>
          )}
        </div>

        {slug && !flow && plans && plans.length === 0 && (
          <FlowPicker slug={slug} brand={meta?.partner_name || meta?.brand_name || slug} upstream={meta?.partner_transfer_upstream} />
        )}
        {(!slug || flow || (plans && plans.length > 0)) && (
        <>
        <StepDots step={step} />

        {step === 0 && !techFilter && (
          <TechPicker onPick={(t) => setTechFilter(t)} />
        )}
        {step === 0 && techFilter && (
          <>
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-slate-400">
                Showing plans for
                <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 text-xs font-semibold px-3 py-1">
                  {NBN_TECH_LABEL[techFilter] || techFilter}
                </span>
              </div>
              <button
                type="button"
                data-testid="tech-change"
                onClick={() => { setTechFilter(""); setForm({ ...form, plan_id: "" }); }}
                className="text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-4"
              >
                Change technology
              </button>
            </div>
          </>
        )}
        {step === 0 && techFilter && (
          <StepPlans
            plans={filteredPlans}
            chosenId={form.plan_id}
            onPick={(id) => setForm({...form, plan_id: id})}
            partnerLabel={slug ? (meta?.partner_name || meta?.partner_short_name || slug) : null}
            partnerColor={meta?.partner_brand_color}
          />
        )}
        {step === 1 && (
          <StepOptions form={form} setForm={setForm} staticIp={staticIp} />
        )}
        {step === 2 && (
          <StepYou form={form} setForm={setForm} />
        )}
        {step === 3 && (
          <StepAddress form={form} setForm={setForm} showAvcField={showAvcField} flow={flow} />
        )}
        {step === 4 && (
          <StepSign form={form} setForm={setForm} plan={chosenPlan} legals={legals} onOpenLegal={() => setLegalOpen(true)} staticIp={staticIp} />
        )}

        <div className="mt-8 flex justify-between">
          <button
            data-testid="wizard-back"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-900 disabled:opacity-30 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              data-testid="wizard-next"
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full font-semibold text-sm"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="wizard-submit"
              onClick={submit}
              disabled={!canNext || busy}
              className="inline-flex items-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3 rounded-full font-semibold text-sm"
            >
              {busy ? "Submitting…" : "Submit signed order"} <Check className="w-4 h-4" />
            </button>
          )}
        </div>

        {legalOpen && (
          <LegalModal docs={legals} onClose={() => setLegalOpen(false)} />
        )}
        </>)}
      </div>
    </div>
  );
}

function FlowPicker({ slug, brand, upstream }) {
  // If the partner has a declared upstream RSP (Admin → Partners →
  // "Transfer upstream provider"), we know the transfer link is *only*
  // for customers currently on that RSP — surface it loudly so someone
  // on a different provider doesn't accidentally click Transfer and get
  // billed against a plan they've never had.
  const upstreamName = (upstream || "").trim();
  const hasUpstream = !!upstreamName;
  return (
    <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="flow-picker">
      <a
        href={`/signup/${encodeURIComponent(slug)}/new`}
        data-testid="flow-pick-new"
        className="group relative overflow-hidden rounded-2xl border border-slate-800 hover:border-cyan-500 bg-slate-900 hover:bg-slate-900/80 p-8 transition-colors"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">01 · New customer</div>
        <h3 className="font-display text-2xl mt-3">I&apos;m getting NBN for the first time</h3>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">Fresh signup — you don&apos;t currently have an internet service to move over. See {brand}&apos;s standard NBN pricing.</p>
        <div className="text-cyan-300 text-sm font-semibold mt-6 inline-flex items-center gap-2">Continue as new customer <span aria-hidden>→</span></div>
      </a>
      <a
        href={`/signup/${encodeURIComponent(slug)}/transfer`}
        data-testid="flow-pick-transfer"
        className={`group relative overflow-hidden rounded-2xl border p-8 transition-colors ${
          hasUpstream
            ? "border-amber-500/50 hover:border-amber-400 bg-slate-900 hover:bg-slate-900/80"
            : "border-slate-800 hover:border-cyan-500 bg-slate-900 hover:bg-slate-900/80"
        }`}
      >
        {hasUpstream && (
          <div
            data-testid="valve-only-pill"
            className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/60 text-amber-200 rounded px-2 py-1 text-[10px] uppercase tracking-widest font-bold"
          >
            <AlertTriangle className="w-3 h-3" /> {upstreamName} customers only
          </div>
        )}
        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">02 · Transferring in</div>
        <h3 className="font-display text-2xl mt-3">I&apos;m already a {brand} customer</h3>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          {hasUpstream
            ? <>Only use this if you&apos;re <span className="text-amber-200 font-semibold">currently a {upstreamName} customer</span> being migrated by {brand}. Your existing plan &amp; price will be mirrored across.</>
            : <>Existing internet customer moving your service to us. See the transfer pricing mirrored from your current plan.</>
          }
        </p>
        <div className={`text-sm font-semibold mt-6 inline-flex items-center gap-2 ${hasUpstream ? "text-amber-300" : "text-cyan-300"}`}>
          {hasUpstream ? `I'm a ${upstreamName} customer — continue` : "Continue as transfer"} <span aria-hidden>→</span>
        </div>
      </a>
    </div>
  );
}

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 mb-10" data-testid="wizard-steps">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2 flex-1">
          <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold border ${
            i < step ? "bg-cyan-500 border-cyan-400 text-slate-950" :
            i === step ? "border-cyan-500 text-cyan-400" : "border-slate-800 text-slate-600"
          }`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
          <span className={`text-xs ${i === step ? "text-slate-100 font-semibold" : "text-slate-500"}`}>{label}</span>
          {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-800" />}
        </div>
      ))}
    </div>
  );
}

// Step 2 of 5 — Options & add-ons. Currently gates on Static IP but
// designed so future add-ons drop in the same way: each is a pair of
// yes/no cards backed by its own tri-state on ``form``. Customer must
// pick every card before Continue enables — no defaults, no accidental
// no-thanks. When the tenant hasn't enabled a given add-on, the whole
// section is skipped so the wizard renders a friendly "no add-ons"
// state and still requires one click through.
function StepOptions({ form, setForm, staticIp }) {
  const price = Number(staticIp?.monthly_price || 0).toFixed(2);
  const hasAny = !!staticIp?.enabled;

  function pickStaticIp(value) {
    setForm({ ...form, static_ip_choice: value, static_ip_addon: value === true });
  }

  return (
    <div className="space-y-8" data-testid="step-options">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold mb-1">
          Step 2 of 5
        </div>
        <h3 className="font-display text-2xl">Options &amp; add-ons</h3>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
          Optional extras that get bolted onto your plan. Pick one option on each card &mdash; either <b>Add</b> or <b>No thanks</b> &mdash; before you continue. You can change your mind on the final step before signing.
        </p>
      </div>

      {staticIp?.enabled && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="option-static-ip">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-500/40 grid place-items-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-100">Static IP address</div>
              <div className="text-xs text-slate-400 mt-1 leading-relaxed">
                Recommended for hosted services, VPN, remote access and CCTV. Adds <b className="text-cyan-300">+${price}/mo</b> on any plan.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="option-static-ip-yes"
              onClick={() => pickStaticIp(true)}
              className={`text-left rounded-xl border p-4 transition-colors ${
                form.static_ip_choice === true
                  ? "border-cyan-400 bg-cyan-500/15 ring-2 ring-cyan-500/40"
                  : "border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-500/5"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-100">Yes, add a static IP</div>
                {form.static_ip_choice === true && <CheckCircle2 className="w-5 h-5 text-cyan-300" />}
              </div>
              <div className="text-xs text-slate-400 mt-1">+${price}/mo added to your plan</div>
            </button>
            <button
              type="button"
              data-testid="option-static-ip-no"
              onClick={() => pickStaticIp(false)}
              className={`text-left rounded-xl border p-4 transition-colors ${
                form.static_ip_choice === false
                  ? "border-slate-400 bg-slate-800/60 ring-2 ring-slate-500/40"
                  : "border-slate-800 hover:border-slate-600 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-100">No thanks</div>
                {form.static_ip_choice === false && <CheckCircle2 className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="text-xs text-slate-400 mt-1">Standard dynamic IP is fine for most home use</div>
            </button>
          </div>
          {form.static_ip_choice === null && (
            <div className="mt-3 text-[11px] text-amber-300 uppercase tracking-widest font-semibold">
              Please pick one option to continue
            </div>
          )}
        </div>
      )}

      {!hasAny && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-center text-slate-400 text-sm">
          No add-ons are configured for this signup flow &mdash; click <b>Continue</b> to move on.
        </div>
      )}
    </div>
  );
}

function StepPlans({ plans, chosenId, onPick, partnerLabel, partnerColor }) {
  if (!plans.length) return <div className="text-slate-500 text-center py-12">No plans available.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {plans.map(p => {
        const chosen = p.id === chosenId;
        return (
          <button
            key={p.id}
            data-testid={`plan-choice-${p.id}`}
            onClick={() => onPick(p.id)}
            className={`text-left rounded-2xl border p-6 transition-all ${
              chosen ? "border-cyan-500 bg-cyan-500/5 ring-2 ring-cyan-500/40" : "border-slate-800 bg-slate-900 hover:border-slate-700"
            }`}
          >
            <Wifi className="w-6 h-6 text-cyan-400 mb-3" />
            <div className="font-display text-xl tracking-tight">{p.name}</div>
            {(p.speed_download_mbps || p.speed_upload_mbps) && (
              <div className="mt-1 text-xs text-slate-500 font-mono">{p.speed_download_mbps || "—"}/{p.speed_upload_mbps || "—"} Mbps</div>
            )}
            {/* Per-plan NBN technology pill row — mirrors the tech
                filter the customer just picked so they can see this
                plan is available on their tech. Empty technologies
                list = "available on any tech" — surfaced as an
                "Any technology" pill so a customer never wonders
                whether the plan is compatible. */}
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {((p.technologies || []).length > 0 ? p.technologies : ["ANY"]).map((t) => (
                <span
                  key={`${p.id}-${t}`}
                  className="inline-flex items-center rounded-full bg-slate-800 text-slate-300 text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 border border-slate-700"
                >
                  {t === "ANY" ? "Any technology" : (t === "FW" ? "Fixed Wireless" : t)}
                </span>
              ))}
            </div>
            {partnerLabel && (
              <div
                data-testid={`plan-partner-tag-${p.id}`}
                className="mt-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-semibold rounded px-1.5 py-0.5 border"
                style={{
                  backgroundColor: (partnerColor || "#22d3ee") + "22",
                  color: partnerColor || "#22d3ee",
                  borderColor: (partnerColor || "#22d3ee") + "55",
                }}
              >
                As offered by {partnerLabel}
              </div>
            )}
            <div className="mt-4 font-display text-3xl">${(p.monthly_price || 0).toFixed(2)}<span className="text-sm text-slate-500 font-normal">/mo</span></div>
            {p.activation_fee > 0 && <div className="text-xs text-slate-500 mt-1">+ ${p.activation_fee.toFixed(2)} activation</div>}
            {p.description && <div className="text-sm text-slate-400 mt-3 leading-relaxed">{p.description}</div>}
            {p.features?.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                {p.features.map((f, i) => (
                  <li key={`${i}-${f}`} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-cyan-400 mt-1 shrink-0" /> {f}</li>
                ))}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
}

function StepYou({ form, setForm }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* v1.10.102: business_name optional for personal customers.
          Contact name is the required identity field. */}
      <TextField label="Business name (if applicable)" value={form.business_name} onChange={(v) => setForm({...form, business_name: v})} testid="you-business" />
      <TextField label="Contact name" required value={form.contact_name} onChange={(v) => setForm({...form, contact_name: v})} testid="you-contact" />
      <TextField label="Email" type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})} testid="you-email" />
      <TextField label="Phone" value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="you-phone" />
      <TextField label="ABN (if applicable)" value={form.abn} onChange={(v) => setForm({...form, abn: v})} testid="you-abn" />
    </div>
  );
}

function StepAddress({ form, setForm, showAvcField, flow }) {
  function handleSelect(components) {
    // Store the structured components alongside the free-text address so
    // downstream (Syncro customer create, PDF interpolation) can use the
    // parts without re-parsing.
    setForm({
      ...form,
      service_address: components.formatted,
      service_address_components: components,
    });
  }

  const isNewConnection = flow === "new";
  const [uploadBusy, setUploadBusy] = useState(false);
  const proof = form.proof_of_address;
  // Today in local YYYY-MM-DD so the date input's min= stops a customer
  // picking a past date. Max is +60 days — further out is almost always
  // a mis-type, and NBN install windows don't quote past 60 days anyway.
  const todayStr = new Date().toISOString().slice(0, 10);
  const maxDateObj = new Date();
  maxDateObj.setDate(maxDateObj.getDate() + 60);
  const maxDateStr = maxDateObj.toISOString().slice(0, 10);

  async function uploadProof(file) {
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp",
                     "image/heic", "image/heif", "application/pdf"];
    if (!allowed.includes((file.type || "").toLowerCase())) {
      toast.error("Please upload a PDF, JPG, PNG, WEBP or HEIC file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large — 10 MB max.");
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/public/signups/upload-proof`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setForm({
        ...form,
        proof_of_address: {
          upload_id: data.upload_id,
          filename: data.filename,
          size_bytes: data.size_bytes,
          content_type: data.content_type,
          token: data.token,
          // storage_path intentionally omitted — SEC-001 (v1.10.136):
          // the server keeps it in the signup_proof_uploads registry
          // and resolves it from upload_id, so the client never sees
          // (or sends) a file path.
        },
      });
      toast.success(`${data.filename} uploaded.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Please try again.");
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <AddressAutocomplete
        label="Service address" required
        value={form.service_address}
        onChange={(v) => setForm({...form, service_address: v})}
        onSelect={handleSelect}
        testid="addr-service"
        placeholder="Start typing your street address…"
      />
      <TextField label="Billing address (if different)" value={form.billing_address} onChange={(v) => setForm({...form, billing_address: v})} testid="addr-billing" />

      <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 space-y-2" data-testid="pref-date-block">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">
            Preferred activation / transfer date
          </span>
          <input
            data-testid="pref-activation-date"
            type="date"
            min={todayStr}
            max={maxDateStr}
            value={form.preferred_activation_date || ""}
            onChange={(e) => setForm({ ...form, preferred_activation_date: e.target.value })}
            className={inputCls + " font-mono"}
          />
        </label>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          ⚠ This is a <strong>requested</strong> date only — it <strong>isn&apos;t guaranteed</strong>. Our team will confirm your actual activation window with the wholesale carrier once we&apos;ve lodged the order. Leave blank if you&apos;re flexible.
        </p>
      </div>

      {isNewConnection && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="proof-upload-block">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-300 mb-1">
              Proof of address (required for new connections)
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Please upload a <strong>rates notice</strong> or a <strong>recent electricity bill</strong> showing your name and this service address. PDF, JPG, PNG or HEIC — 10 MB max.
            </p>
          </div>
          {proof?.upload_id ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2" data-testid="proof-uploaded-pill">
              <div className="flex items-center gap-2 text-emerald-200 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-mono text-xs">{proof.filename}</span>
                <span className="text-[11px] text-emerald-300/70">
                  ({Math.round((proof.size_bytes || 0) / 1024)} KB)
                </span>
              </div>
              <button
                type="button"
                data-testid="proof-remove"
                onClick={() => setForm({ ...form, proof_of_address: null })}
                className="text-xs text-emerald-200 hover:text-white underline underline-offset-4"
              >
                Replace
              </button>
            </div>
          ) : (
            <label
              data-testid="proof-upload-label"
              className={`flex items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
                uploadBusy
                  ? "border-amber-500/30 bg-amber-500/5 opacity-60 cursor-wait"
                  : "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/60"
              }`}
            >
              {uploadBusy ? (
                <>
                  <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />
                  <span className="text-sm text-amber-200">Uploading…</span>
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5 text-amber-300 rotate-[-90deg]" />
                  <span className="text-sm text-amber-200">
                    Tap to upload rates notice or electricity bill
                  </span>
                </>
              )}
              <input
                type="file"
                accept="image/*,application/pdf"
                data-testid="proof-upload-input"
                disabled={uploadBusy}
                onChange={(e) => uploadProof(e.target.files?.[0])}
                className="hidden"
              />
            </label>
          )}
        </div>
      )}

      {showAvcField && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-2" data-testid="avc-field-block">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-cyan-300 mb-1.5 block">
              AVC number <span className="text-slate-500 normal-case tracking-normal">(only if you&apos;re transferring from another provider)</span>
            </span>
            <input
              data-testid="you-avc"
              type="text"
              value={form.avc_number || ""}
              onChange={(e) => setForm({ ...form, avc_number: e.target.value.toUpperCase() })}
              placeholder="AVC000123456789"
              className={inputCls + " font-mono tracking-wider"}
              maxLength={20}
              autoComplete="off"
            />
          </label>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Only needed if you already have an NBN service with another provider and want us to port it across. Leave blank for a fresh install — we&apos;ll arrange a new NBN connection at your address. Your AVC number is on any recent bill from your current provider (format: <span className="font-mono text-slate-300">AVC</span> followed by 9–15 digits).
          </p>
        </div>
      )}
    </div>
  );
}

function StepSign({ form, setForm, plan, legals, onOpenLegal, staticIp }) {
  const addonPrice = Number(staticIp?.monthly_price || 0);
  const planPrice = Number(plan?.monthly_price || 0);
  const total = planPrice + (form.static_ip_addon ? addonPrice : 0);
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Order summary</div>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-xl">{plan?.name}</div>
            <div className="text-sm text-slate-400 mt-1">{form.business_name || form.contact_name} · {form.email}</div>
            <div className="text-xs text-slate-500 mt-0.5">{form.service_address}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl text-cyan-400">${total.toFixed(2)}<span className="text-sm text-slate-500">/mo</span></div>
            {plan?.activation_fee > 0 && <div className="text-xs text-slate-500">+ ${plan.activation_fee.toFixed(2)} once-off</div>}
            {form.static_ip_addon && (
              <div className="text-[11px] text-amber-300 mt-1">Includes static IP · +${addonPrice.toFixed(2)}/mo</div>
            )}
          </div>
        </div>
        {/* Static IP addon toggle — kept on the sign step as a
            last-chance mind-changer. The explicit yes/no is captured on
            the Options step (step 2 of 5); flipping this checkbox
            here updates both the boolean and the tri-state choice so
            the summary + submit body stay consistent. */}
        {staticIp?.enabled && (
          <label className="mt-5 pt-4 border-t border-slate-800 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="signup-static-ip"
              checked={!!form.static_ip_addon}
              onChange={(e) => setForm({
                ...form,
                static_ip_addon: e.target.checked,
                static_ip_choice: e.target.checked,
              })}
              className="mt-1"
            />
            <div>
              <div className="text-sm text-slate-200 font-medium">
                Add a static IP address &mdash; +${addonPrice.toFixed(2)}/mo
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Recommended for hosted services, VPN, remote access, and CCTV. Applies to any plan.
              </div>
            </div>
          </label>
        )}
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Terms & sign-off</div>
        </div>
        <div className="text-sm text-slate-300 mb-4">
          By ticking below and typing your full name, you agree to our{" "}
          <button type="button" data-testid="open-legal-modal" onClick={onOpenLegal} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-4">
            {legals.map(l => l.title).join(", ")}
          </button>. A signed PDF of these terms will be emailed to you and stored against your account.
        </div>
        <TextField label="Type your full name to sign" required value={form.typed_name} onChange={(v) => setForm({...form, typed_name: v})} testid="sign-typed-name" placeholder="Jane Doe" />
        <SignatureCanvas value={form.drawn_data_url} onChange={(d) => setForm({...form, drawn_data_url: d})} />
        <label className="mt-4 flex items-start gap-2 cursor-pointer">
          <input type="checkbox" data-testid="sign-agree" checked={form.agreed} onChange={(e) => setForm({...form, agreed: e.target.checked})} className="mt-1" />
          <span className="text-sm text-slate-300">
            I have read and agree to the above documents. I understand a signed PDF will be sent to my email address.
          </span>
        </label>
      </div>
    </div>
  );
}

function SignatureCanvas({ value, onChange }) {
  const [drawing, setDrawing] = useState(false);
  // Canvas has a fixed internal resolution (600×140) but is styled
  // ``w-full`` — so the CSS box is stretched. Pointer coordinates come
  // in CSS pixels; we need to scale them into canvas coordinates so the
  // ink lands under the cursor.
  function scaledPoint(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const p = "touches" in e && e.touches.length ? e.touches[0]
              : "changedTouches" in e && e.changedTouches.length ? e.changedTouches[0]
              : e;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (p.clientX - rect.left) * scaleX,
      y: (p.clientY - rect.top) * scaleY,
    };
  }
  function start(e) {
    setDrawing(true);
    const canvas = e.currentTarget;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    const { x, y } = scaledPoint(e, canvas);
    ctx.moveTo(x, y);
  }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const canvas = e.currentTarget;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2; ctx.strokeStyle = "#e2e8f0"; ctx.lineCap = "round";
    const { x, y } = scaledPoint(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function end(e) {
    if (!drawing) return;
    setDrawing(false);
    onChange(e.currentTarget.toDataURL("image/png"));
  }
  function clear() {
    const c = document.getElementById("sig-canvas");
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(null);
  }
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Draw signature <span className="text-red-400">*</span>
        </span>
        {value && <button type="button" onClick={clear} className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1" data-testid="sign-clear"><Eraser className="w-3 h-3" /> Clear</button>}
      </div>
      <canvas
        id="sig-canvas" data-testid="signature-canvas"
        width={600} height={140}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className={`w-full rounded-lg border ${value ? "border-slate-800" : "border-red-500/40"} bg-slate-950 cursor-crosshair touch-none`}
      />
      <div className="mt-1 text-[10px] text-slate-500 inline-flex items-center gap-1">
        <PenTool className="w-3 h-3" /> Use your finger or mouse to sign. Both a signature and a typed name are required.
      </div>
    </div>
  );
}

function LegalModal({ docs, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[85vh] rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="legal-modal">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <h3 className="font-display text-xl tracking-tight">Terms & conditions</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 text-sm">Close</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 prose prose-invert prose-sm max-w-none">
          {docs.map(d => (
            <section key={d.slug} className="mb-8">
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">v{d.version}</div>
              <div dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(d.body_html || "", {
                  // T&Cs need typical rich-text markup (lists, tables,
                  // inline formatting, links) — but nothing executable.
                  // DOMPurify's html profile strips <script>, event
                  // handlers and dangerous URL schemes automatically.
                  USE_PROFILES: { html: true },
                }),
              }} />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      <input data-testid={testid} type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </label>
  );
}

function SuccessCard({ result, onBack }) {
  const [modalOpen, setModalOpen] = useState(true);
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center p-6">
      <div className="max-w-lg w-full rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="signup-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
        <h2 className="font-display text-2xl tracking-tight mb-2">Signed and sent.</h2>
        <p className="text-slate-300 mb-4">
          Your signed order and a copy of the terms have been emailed to you.
        </p>
        {result?.syncro_ticket_number && (
          <p className="text-xs text-slate-500 font-mono">Reference: ticket #{result.syncro_ticket_number}</p>
        )}
        <button onClick={onBack} className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="w-4 h-4" /> Home</button>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm grid place-items-center p-4 animate-in fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-modal-title"
          data-testid="signup-callus-modal"
        >
          <div className="max-w-lg w-full rounded-2xl border-2 border-amber-500 bg-slate-900 shadow-2xl shadow-amber-500/20 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-500/15 border-2 border-amber-500/60 grid place-items-center mx-auto mb-4">
              <ShieldCheck className="w-7 h-7 text-amber-300" />
            </div>
            <h3
              id="cc-modal-title"
              className="font-display text-2xl md:text-3xl tracking-tight text-amber-100 mb-3"
            >
              One last step — please call us
            </h3>
            <p className="text-slate-200 mb-5 leading-relaxed">
              To finish your signup we need to take your payment method securely over
              the phone.{" "}
              <span className="text-amber-200 font-semibold">
                For your security, do NOT email your credit-card details.
              </span>
            </p>
            <div className="space-y-3 mb-6">
              <a
                href="tel:0268523204"
                data-testid="callus-phone-1"
                className="block rounded-xl border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 py-4 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Local</div>
                <div className="font-display text-3xl tracking-tight text-amber-100">02 6852 3204</div>
              </a>
              <a
                href="tel:1300938499"
                data-testid="callus-phone-2"
                className="block rounded-xl border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 py-4 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Australia-wide</div>
                <div className="font-display text-3xl tracking-tight text-amber-100">1300 938 499</div>
              </a>
            </div>
            <p className="text-xs text-slate-400 mb-5 leading-relaxed">
              Your service <span className="text-slate-200 font-medium">cannot be activated</span> until
              payment details are captured. We&apos;ll never ask for card details by email.
            </p>
            <button
              onClick={() => setModalOpen(false)}
              data-testid="callus-modal-close"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold transition-colors"
            >
              I&apos;ll call now — got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
